import { RenderEngine } from 'application-frame/rendering';
import DataBinding from '@af-modules/databinding';
import ViewController from '@af-modules/databinding/prototypes/ViewController';

import AppPageHead from '../modules/AppPageHead';
import Transition from '../rendering/Transition';
import flip from '../flip';

const projectViewData = new WeakMap();

const MainPage = {

    _isInitDone: false,

    template: 'main-page',
    route: '/home',
    scope: null,
    isActive: false,
    modalPlaceholder: null,
    showBackdrop: null,
    selectedTab: 0,
    currentModal: null,

    projectList: [{
        title: 'Application Frame',
        description: 'Application Frame is a small JavaScript Framework written in ES6, aiming to provide simple and fast tools to create rich Applications.',
        descriptionLong: `<p>Application Frame is a small JavaScript
                            Framework written in ES6, aiming to provide simple and fast tools to create rich applications.</p>

                            <table>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>v1.0</th>
                                        <th>v2.0</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Release</td>
                                        <td>delayed</td>
                                        <td>to be planed</td>
                                    </tr>
                                    <tr>
                                        <td>State</td>
                                        <td>under development</td>
                                        <td>waiting to be outlined</td>
                                    </tr>
                                </tbody>
                            </table>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Module</th>
                                        <th>Version</th>
                                        <th>Package Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>af-router</td>
                                        <td>v0.2.x</td>
                                        <td>@af-modules/router</td>
                                    </tr>
                                    <tr>
                                        <td>af-databinding</td>
                                        <td>v1.1.x</td>
                                        <td>@af-modules/databinding</td>
                                    </tr>
                                    <tr>
                                        <td>af-compiler</td>
                                        <td>v1.2.x</td>
                                        <td>@af-modules/compiler</td>
                                    </tr>
                                </tbody>
                            </table>`,
        links: [{ name: 'GitHub Page', url: 'https://github.com/TitanNanoDE/ApplicationFrame' }],
    }, {
        title: 'Gulp Bundle Templates',
        description: 'Bundle your HTML templates directly into your main HTML file.',
        descriptionLong: `<p>Bundle your HTML templates directly into your main HTML file.</p>

                        <p>With this module HTML templates can be easily included into other html files.
                        Therefore html can be split into multiple files and then be merged back into one file,
                        ready to be executed. Ids from the import reference are applied to the imported template tag.</p>

                        <h3>Dependencies</h3>
                        <ul>
                            <li><a href="https://github.com/rvagg/through2">Through2</a></li>
                            <li><a href="https://github.com/tmpvar/jsdom">JSDOM</a></li>
                        </ul>`,
        links: [{
            name: 'Github Page',
            url: 'https://github.com/TitanNanoDE/gulp-bundle-templates',
        }, {
            name: 'Report a Bug',
            url: 'https://github.com/TitanNanoDE/gulp-bundle-templates/issues/new',
        }],
    }, {
        title: 'Vocabulary Trainer',
        description: 'Vocabulary Trainer web application using Application Frame and Polymer.',
        descriptionLong: `<p>Vocabulary Trainer web application using Application Frame and Polymer.</p>

                            <p>A small experiment to write an application with Polymer. Vocabulary Trainer uses
                            the Application Frame and Googles Polymer to create a nice and dynamic UI.<br>
                            This application is based on a software project initially developed as an university assignment.
                            This is also one of the first projects that used the application frame databinding library.</p>`,

        links: [{
            name: 'Try It',
            url: 'https://titannano.github.io/VocabularyTrainer',
        }, {
            name: 'Github Page',
            url: 'https://github.com/TitanNano/VocabularyTrainer',
        }],
    }, {
        title: 'Goal Assistant',
        description: 'Intelligent Productivity Tool for goal driven working.',
        descriptionLong: `<p>Intelligent Productivity Tool for goal driven working.</p>
                            <p>The current state of this Project is still work in progress.
                            A prototype has been realized which allows the user to create
                            and manage his goals. A proper notification system
                            as well as other features are still missing. Also a
                            general fine-tuning and dogfooding has to be done before a public release will be made.</p>`,
        links: [{
            name: 'Github Page',
            url: 'https://github.com/TitanNano/GoalAssistant',
        }]
    }],

    _appPageHead: null,

    get notShowBackdrop() {
        if (this.showBackdrop === null) {
            return '';
        }

        return !this.showBackdrop;
    },

    get appPageHead() {
        if (!this._appPageHead) {
            return AppPageHead.useInAutoBinding();
        }

        return this._appPageHead;
    },

    set appPageHead(value) {
        this._appPageHead = value;
    },

    constructor() {
        const template = `#${this.template}`;

        this.scope =
            DataBinding.createTemplateInstance({ template: template, scope: this }).scope;
        this._isInitDone = true;
    },

    onRouteEnter() {
        if (!this._isInitDone) {
            this.constructor();
        }

        this.isActive = true;
        this.scope.update();
    },

    onRouteLeave() {
        this.isActive = false;
        this.scope.update();
    },

    /**
     * [getProjectViewData description]
     *
     * @param  {{ title: string, description: string, descriptionLong: string, links: string[]}} project [description]
     * @return {object}         [description]
     */
    getProjectViewData(project) {
        if (!projectViewData.has(project)) {
            projectViewData.set(project, {});
        }

        return projectViewData.get(project);
    },

    /**
     * [transitionCard description]
     *
     * @param  {E} element [description]
     * @return {[type]}         [description]
     */
    transitionCard(element) {
        return Transition(element, { target: 'paper-ripple' }).then(() => {
            return Transition(element).add('transitioning');
        }).then(() => {
            const { modalPlaceholder } = this;
            this.showBackdrop = true;
            this.scope.update();

            return flip({
                target: element,
                actor: element.firstElementChild,
                targetClasses: ['modal'],
                properties: ['box-shadow'],
                whenFirstDone() {
                    modalPlaceholder.classList.add('active');
                    element.parentNode.insertBefore(modalPlaceholder, element);
                }
            });
        }).then(() => {
            return Transition(element).remove('transitioning');
        });
    },

    transitionCardBack(element) {
        return Transition(element).add('transitioning').then(() => {
            const { modalPlaceholder } = this;

            this.showBackdrop = false;
            this.scope.update();

            return flip({
                target: element,
                actor: element.firstElementChild,
                targetClasses: ['transition-back'],
                removeClasses: ['modal'],
                properties: ['box-shadow'],
                whenFirstDone() {
                    modalPlaceholder.classList.remove('active');
                }
            });
        }).then(() => {
            element.classList.remove('transition-back');

            return Transition(element).remove('transitioning');
        });
    },


    fadeInBackdrop(element) {
        return new Promise((done) => {
            element.classList.add('animating');

            RenderEngine.skipFrame().scheduleRenderTask(() => {
                Transition(element).remove('animating').then(done);
            });
        });
    },

    fadeOutBackdrop(element) {
        return new Promise(done => {
            element.classList.add('active');

            RenderEngine.skipFrame().scheduleRenderTask(() => {
                Transition(element).add('animating').then(() => {
                    element.classList.remove('animating', 'active');
                    done();
                });
            });
        });
    },

    onCardClick(event, scope) {
        this.currentModal = this.getProjectViewData(scope.project);
        this.currentModal.isModal = true;
    },

    onCloseModal(event, scope) {
        if (event.target.nodeName.toLowerCase() === 'paper-button') {
            return Transition(event.target, { target: 'paper-ripple' })
                .then(() => {
                    this.currentModal.isModal = false;
                    scope.update();
                });
        } else {
            this.currentModal.isModal = false;
        }
    },

    projectIsNotModal(project) {
        const { isModal } = this.getProjectViewData(project);

        if (isModal === undefined || isModal === null) {
            return null;
        }

        return !isModal;
    },

    onOpenLink(event) {
        const target = event.target;
        const tab = window.open('');

        event.stopPropagation();
        event.preventDefault();
        window.focus();

        Transition(target, { target: 'paper-ripple' }).then(() => {
            tab.location = target.parentElement.href;
        });
    },

    isTabActive(tabId) {
        return tabId.toString() === this.selectedTab.toString();
    },

    __proto__: ViewController,
};

export default MainPage;



//////////////////
// WEBPACK FOOTER
// ./pages/MainPage.js
// module id = 54
// module chunks = 0